// Types et appels de l'API de la messagerie (H2). Les droits sont toujours évalués par le serveur ;
// l'interface ne fait que refléter ce qu'il autorise (libellés, capacités et listes viennent de lui).

import { api } from '../../api/client';

export type TypeConversation = 'individuelle' | 'groupe' | 'service' | 'crise' | 'annonces';

export interface ResumeConversation {
  id: string;
  type: TypeConversation;
  /** Libellé du type, ou de l'objet rattaché pour un canal de service. */
  libelleType: string;
  titre: string;
  objetType: string | null;
  objetId: string | null;
  statut: string;
  derniereActiviteLe: string;
  nonLus: number;
  /** Conversation individuelle : nom de l'autre personne. */
  interlocuteur: string | null;
  dernierMessage: { extrait: string; nature: string; auteur: string | null; creeLe: string } | null;
}

export interface ParticipantVue {
  utilisateurId: string;
  role: string;
  dernierLuLe: string | null;
  prenom: string;
  nom: string;
  fonction: string | null;
}

export interface DetailConversation {
  id: string;
  type: TypeConversation;
  libelleType: string;
  libelleObjet: string | null;
  organisationEmettrice: string | null;
  organisationsParties: string[];
  nombreOrganisationsParties: number | null;
  titre: string;
  objetType: string | null;
  objetId: string | null;
  criseId: string | null;
  provinceCode: string | null;
  statut: string;
  archiveeLe: string | null;
  derniereActiviteLe: string;
  monRole: string;
  peutEcrire: boolean;
  motifLecture: string | null;
  peutConsignerDecision: boolean;
  peutGererParticipants: boolean;
  participants: ParticipantVue[];
}

export interface PieceJointeVue {
  id: string;
  nomFichier: string;
  typeFichier: string;
  taille: number;
}

export interface MessageVue {
  id: string;
  nature: 'ordinaire' | 'notification' | 'decision';
  contenu: string | null;
  creeLe: string;
  auteur: { id: string; nom: string; organisation: string | null } | null;
  modifie: boolean;
  modifieLe: string | null;
  masque: { le: string; par: string | null; motif: string | null } | null;
  cite: { id: string; auteur: string | null; extrait: string | null } | null;
  decision: {
    titre: string | null;
    responsable: string | null;
    echeance: string | null;
    decisionId: string | null;
    corrigeeId: string | null;
  } | null;
  piecesJointes: PieceJointeVue[];
  lecture: {
    luPar: { utilisateurId: string; nom: string; le: string }[];
    lecteurs: number;
    destinataires: number;
  } | null;
  modifiable: boolean;
  masquable: boolean;
}

export interface DecisionVue {
  id: string;
  titre: string | null;
  extrait: string;
  responsable: string | null;
  echeance: string | null;
  creeLe: string;
  auteur: string | null;
  organisation: string | null;
  decisionId: string | null;
  corrigeeId: string | null;
  corrigeePar: string[];
}

export interface ResultatRecherche {
  messageId: string;
  conversationId: string;
  conversationTitre: string;
  conversationType: TypeConversation;
  nature: string;
  creeLe: string;
  auteur: string | null;
  extrait: string;
}

export interface Contact {
  id: string;
  prenom: string;
  nom: string;
  fonction: string | null;
  organisationId: string;
  organisation: string;
}

export interface StatutMessagerie {
  disponible: boolean;
  nonLus: number;
  conversationsNonLues: number;
  emissionAnnonces: {
    organisations: { id: string; nom: string }[];
    typesDestinataires: { code: string; libelle: string; nombre: number }[];
  } | null;
}

export const cles = {
  statut: ['messagerie', 'statut'] as const,
  conversations: (archivees: boolean) => ['messagerie', 'conversations', archivees] as const,
  conversation: (id: string) => ['messagerie', 'conversation', id] as const,
  messages: (id: string) => ['messagerie', 'messages', id] as const,
  decisions: (id: string) => ['messagerie', 'decisions', id] as const,
};

export async function lireStatut() {
  return (await api<StatutMessagerie>('/api/v1/messagerie/statut')).donnees;
}

export async function listerConversations(archivees: boolean) {
  return api<ResumeConversation[]>(`/api/v1/conversations${archivees ? '?archivees=true' : ''}`);
}

export async function lireConversation(id: string) {
  return (await api<DetailConversation>(`/api/v1/conversations/${id}`)).donnees;
}

export async function lireMessages(id: string, curseur?: string | null) {
  const p = new URLSearchParams({ limite: '40' });
  if (curseur) p.set('curseur', curseur);
  return api<MessageVue[]>(`/api/v1/conversations/${id}/messages?${p.toString()}`);
}

export async function lireDecisions(id: string) {
  return (await api<DecisionVue[]>(`/api/v1/conversations/${id}/decisions`)).donnees;
}

export async function envoyerMessage(
  id: string,
  corps: {
    contenu: string;
    citeMessageId?: string | null;
    piecesJointes?: string[];
    identifiantClient: string;
  },
) {
  return (await api<{ id: string }>(`/api/v1/conversations/${id}/messages`, { corps })).donnees;
}

export async function deposerPieceJointe(conversationId: string, fichier: File) {
  const f = new FormData();
  f.append('fichier', fichier);
  return (
    await api<PieceJointeVue & { statut: string }>(`/api/v1/conversations/${conversationId}/pieces-jointes`, {
      formulaire: f,
    })
  ).donnees;
}

/** Lien signé de 5 minutes, lié à l'utilisateur ; les droits sont revérifiés par le serveur à l'accès. */
export async function lienPieceJointe(id: string) {
  return (await api<{ url: string; expireLe: string }>(`/api/v1/pieces-jointes/${id}/lien`, { corps: {} }))
    .donnees;
}

export async function telechargerPieceJointe(id: string) {
  const lien = await lienPieceJointe(id);
  window.location.assign(lien.url);
}

export async function marquerLue(id: string) {
  return api(`/api/v1/conversations/${id}/lecture`, { corps: {} });
}

export async function modifierMessage(id: string, contenu: string) {
  return api(`/api/v1/messages/${id}`, { methode: 'PATCH', corps: { contenu } });
}

export async function masquerMessage(id: string, motif: string) {
  return api(`/api/v1/messages/${id}/masquage`, { corps: { motif } });
}

export async function historiqueMessage(id: string) {
  return (
    await api<{ id: string; contenuPrecedent: string; contenuNouveau: string; modifieLe: string }[]>(
      `/api/v1/messages/${id}/historique`,
    )
  ).donnees;
}

export async function consignerDecision(
  conversationId: string,
  d: {
    titre: string;
    contenu: string;
    responsable: string;
    echeance: string;
    decisionCorrigeeId?: string | null;
  },
) {
  return api(`/api/v1/conversations/${conversationId}/decisions`, { corps: { ...d, confirmation: true } });
}

export async function rechercher(q: string) {
  return (await api<ResultatRecherche[]>(`/api/v1/messagerie/recherche?q=${encodeURIComponent(q)}`)).donnees;
}

export async function contacts(q: string) {
  return (await api<Contact[]>(`/api/v1/messagerie/contacts${q ? `?q=${encodeURIComponent(q)}` : ''}`))
    .donnees;
}

export async function collegues() {
  return (await api<Contact[]>('/api/v1/messagerie/collegues')).donnees;
}

export async function creerConversation(corps: Record<string, unknown>) {
  return (await api<{ id: string }>('/api/v1/conversations', { corps })).donnees;
}

export async function ajouterParticipant(conversationId: string, utilisateurId: string, role: string) {
  return api(`/api/v1/conversations/${conversationId}/participants`, { corps: { utilisateurId, role } });
}

export async function retirerParticipant(conversationId: string, utilisateurId: string) {
  return api(`/api/v1/conversations/${conversationId}/participants/${utilisateurId}/retrait`, { corps: {} });
}

/** Taille lisible d'un fichier. */
export function taille(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} ko`;
  return `${(octets / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
}

/** Découpe un extrait de recherche marqué [[...]] en segments, sans HTML injecté. */
export function segmentsExtrait(texte: string): { texte: string; marque: boolean }[] {
  const segments: { texte: string; marque: boolean }[] = [];
  const motif = /\[\[(.*?)\]\]/g;
  let dernier = 0;
  for (const m of texte.matchAll(motif)) {
    if (m.index > dernier) segments.push({ texte: texte.slice(dernier, m.index), marque: false });
    segments.push({ texte: m[1] ?? '', marque: true });
    dernier = m.index + m[0].length;
  }
  if (dernier < texte.length) segments.push({ texte: texte.slice(dernier), marque: false });
  return segments;
}

const FUSEAU = 'Africa/Libreville';
const FORMAT_HEURE = new Intl.DateTimeFormat('fr-FR', {
  timeZone: FUSEAU,
  hour: '2-digit',
  minute: '2-digit',
});
const FORMAT_JOUR_SEMAINE = new Intl.DateTimeFormat('fr-FR', { timeZone: FUSEAU, weekday: 'long' });
const FORMAT_JOUR_COURT = new Intl.DateTimeFormat('fr-FR', {
  timeZone: FUSEAU,
  day: 'numeric',
  month: 'short',
});
const FORMAT_JOUR_LONG = new Intl.DateTimeFormat('fr-FR', {
  timeZone: FUSEAU,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const FORMAT_CLE_JOUR = new Intl.DateTimeFormat('fr-CA', {
  timeZone: FUSEAU,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Jour calendaire en heure du Gabon (clé de regroupement). */
export function cleJour(iso: string | Date): string {
  return FORMAT_CLE_JOUR.format(typeof iso === 'string' ? new Date(iso) : iso);
}

function ecartJours(iso: string, maintenant: Date): number {
  const a = Date.parse(`${cleJour(iso)}T00:00:00Z`);
  const b = Date.parse(`${cleJour(maintenant)}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/** Date courte des listes : heure aujourd'hui, « hier », jour de la semaine, puis date. */
export function dateCourte(iso: string, maintenant = new Date()): string {
  const ecart = ecartJours(iso, maintenant);
  if (ecart <= 0) return FORMAT_HEURE.format(new Date(iso));
  if (ecart === 1) return 'hier';
  if (ecart < 7) return FORMAT_JOUR_SEMAINE.format(new Date(iso));
  return FORMAT_JOUR_COURT.format(new Date(iso));
}

/** Intitulé d'un séparateur de jour dans le fil. */
export function libelleJour(iso: string, maintenant = new Date()): string {
  const ecart = ecartJours(iso, maintenant);
  if (ecart <= 0) return 'Aujourd’hui';
  if (ecart === 1) return 'Hier';
  return FORMAT_JOUR_LONG.format(new Date(iso));
}

export function heureDe(iso: string): string {
  return FORMAT_HEURE.format(new Date(iso));
}

/** Pictogramme textuel du type de fichier (jamais une couleur seule). */
export function codeFichier(type: string): string {
  return type === 'jpeg' ? 'JPG' : type.toUpperCase();
}
